import React from 'react';
import { CheckIcon, EyeIcon, LayoutGridIcon, MessageSquareTextIcon, SlidersHorizontalIcon, TargetIcon, TimerIcon } from 'lucide-react';
import { SectionHeading } from '../ui/SectionHeading';
import { CtaButton } from '../ui/CtaButton';
import { COVER_IMAGE, coreGuideFacts } from '../../data/site';
import { guideFeatures, GuideFeatureIcon, teachingSteps } from '../../data/guideFeatures';
const icons: Record<GuideFeatureIcon, typeof TargetIcon> = {
  target: TargetIcon,
  message: MessageSquareTextIcon,
  layout: LayoutGridIcon,
  timer: TimerIcon,
  sliders: SlidersHorizontalIcon,
  eye: EyeIcon
};
export function CoreContent() {
  return <section aria-labelledby="content-title" className="bg-white py-14 text-ink sm:py-20 lg:py-24" data-id="element-127" data-mp="134ol8blfm4qe">
      <div className="mx-auto max-w-page px-5 lg:px-8" data-id="element-128" data-mp="183rsm2ovwn1j">
        <SectionHeading id="content-title" tone="light" eyebrow="The core guide" lead="Everything you need" highlight="to teach the unit" data-id="element-129" data-mp="u74guts9vsbk" />

        <article className="mt-10 grid items-center gap-6 rounded-xl border border-[#E5E7EB] p-5 sm:mt-12 sm:gap-8 sm:p-8 md:grid-cols-[220px_1fr] md:gap-10 lg:p-10" data-id="element-130" data-mp="1fp6evvty8slt">
          <div className="mx-auto w-40 md:w-full" data-id="element-131" data-mp="1po5dell037v3">
            <img src={COVER_IMAGE} alt="Cover of the Badminton Class Ready guide" loading="lazy" className="block h-auto w-full shadow-[0_18px_36px_-16px_rgba(0,0,0,0.45)]" data-id="element-132" data-mp="1pnvn0ww1myov" />
          </div>
          <div data-id="element-133" data-mp="s15amz3szyc7">
            <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold-ink" data-id="element-134" data-mp="s1oi1lvtv4bz">Core guide · Digital PDF</p>
            <h3 className="mt-2 font-display text-[30px] font-bold uppercase leading-[1.02] sm:text-[38px]" data-id="element-135" data-mp="16r45ot02atng">
              12 Ready-to-Teach School Badminton Lessons
            </h3>
            <p className="mt-3 text-base leading-relaxed text-[#374151] sm:text-lg" data-id="element-136" data-mp="e86za3aytvn0">
              One visual guide that takes your class from first grip to cooperative rallies and adapted games, one 40-minute lesson
              at a time.
            </p>
            <ul className="mt-6 grid gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-137" data-mp="2ebhvmrkda72z">
              {coreGuideFacts.map(fact => <li key={fact} className="flex gap-2.5 text-base" data-id="element-138" data-mp="2j1ol0tmpdvma">
                  <CheckIcon aria-hidden="true" strokeWidth={3} className="mt-1 h-4 w-4 shrink-0 text-gold-dark" data-id="element-139" data-mp="2j4ki6fqq8g9o" />
                  {fact}
                </li>)}
            </ul>
          </div>
        </article>

        <h3 className="mt-12 text-center font-display text-[28px] font-bold uppercase leading-none sm:mt-16 sm:text-[36px]" data-id="element-140" data-mp="1vc3y5znfie7q">
          In every lesson
        </h3>
        <ul className="mt-7 grid gap-3 sm:mt-8 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 lg:gap-5" data-id="element-141" data-mp="1zgbqs7x0ceb8">
          {guideFeatures.map(feature => {
          const Icon = icons[feature.icon];
          return <li key={feature.label} className="flex gap-4 rounded-xl border border-[#E5E7EB] p-4 sm:p-6" data-id="element-142" data-mp="3fsh0pq5q5lnj">
                <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg sm:h-12 sm:w-12" style={{
              backgroundColor: feature.bg,
              color: feature.fg
            }} data-id="element-143" data-mp="3pw4h83mkrfj8">
                  <Icon className="h-6 w-6" strokeWidth={2.25} data-id="element-144" data-mp="3g4rqxggfcxfb" />
                </span>
                <div data-id="element-145" data-mp="3c4hjs0gwc0yv">
                  <h4 className="text-[13px] font-bold uppercase tracking-[0.16em] text-gold-ink" data-id="element-146" data-mp="3c2wou9qo6g0h">{feature.label}</h4>
                  <p className="mt-1.5 text-base leading-relaxed text-[#374151]" data-id="element-147" data-mp="326tcyc60n30c">{feature.text}</p>
                </div>
              </li>;
        })}
        </ul>

        <div className="mt-12 rounded-xl bg-mint px-5 py-8 sm:mt-16 sm:px-10 sm:py-10" data-id="element-148" data-mp="2x9t63z1cdw86">
          <h3 className="text-center font-display text-[30px] font-bold uppercase leading-none sm:text-[36px]" data-id="element-149" data-mp="2t7y6cyz27qwi">
            How it works
          </h3>
          <ol className="mt-8 grid gap-8 md:grid-cols-3 md:gap-8" data-id="element-150" data-mp="re8pqehxg4vu">
            {teachingSteps.map((step, index) => <li key={step.title} className="flex gap-4 md:flex-col md:gap-3" data-id="element-151" data-mp="yj02arrho21j" data-mpi={index}>
                <span aria-hidden="true" className="flex h-12 w-12 shrink-0 items-center justify-center bg-forest font-display text-[26px] font-bold text-gold" data-id="element-152" data-mp="13hkz0h7j83l8">
                  {index + 1}
                </span>
                <div data-id="element-153" data-mp="tkq6cexxwxev">
                  <h4 className="font-display text-[24px] font-bold uppercase leading-tight" data-id="element-154" data-mp="fqyugzwql1fz">
                    <span className="sr-only" data-id="element-155" data-mp="khxf2borclp1">Step {index + 1}: </span>
                    {step.title}
                  </h4>
                  <p className="mt-1 text-base leading-relaxed text-[#374151]" data-id="element-156" data-mp="5uc8u8wl66cw">{step.text}</p>
                </div>
              </li>)}
          </ol>
        </div>

        <div className="mt-12 text-center" data-id="element-157" data-mp="3sxh1oruxb5v4">
          <CtaButton target="offer" className="w-full sm:w-auto" data-id="element-158" data-mp="3syip7vt4lktq">
            Get the lesson pack
          </CtaButton>
        </div>
      </div>
    </section>;
}